/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useNavigate } from "react-router-dom";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { useSnackbar } from "notistack";
import { PARTY_NUMBER } from "app/routes/constant";
import { createPartyNumber } from "app/redux/slice/partyNumberSlice";
import { getUserList } from "app/redux/slice/userSlice";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const phoneSchema = Yup.string()
  .trim()
  .required("Please enter phone number")
  .matches(/^[0-9]+$/, "Phone number must contain only digits")
  .length(10, "Phone number must be exactly 10 digits");

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Please enter party name"),
  phoneNumbers: Yup.array()
    .of(phoneSchema)
    .min(1, "At least one phone number is required"),
  city: Yup.string().trim(),
  belongsTo: Yup.object().nullable().required("Please select belongs to user"),
});

const AddPartyNumber = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const userData = useSelector((state) => state?.user);
  const mobileView = useMediaQuery("(max-width:575px)");

  useEffect(() => {
    dispatch(getUserList({ sort: 1, field: "name" }));
  }, []);

  return (
    <>
      <Card>
        <CardHeader title="Add Party Number" />
        <CardContent>
          <Formik
            initialValues={{
              name: prefill.name || "",
              phoneNumbers: prefill.phoneNumbers?.length
                ? prefill.phoneNumbers
                : [""],
              city: prefill.city || "",
              belongsTo: prefill.belongsTo || null,
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={async (data, { setSubmitting }) => {
              setSubmitting(true);
              try {
                const payload = {
                  name: data.name.trim(),
                  phoneNumbers: data.phoneNumbers.map((p) => p.trim()),
                  city: data.city?.trim(),
                  belongsTo: data.belongsTo?._id,
                  ...(prefill.leadId && { leadId: prefill.leadId }),
                };

                const response = await dispatch(
                  createPartyNumber(payload),
                ).unwrap();

                if (response.status === 200 || response.status === 201) {
                  enqueueSnackbar(response.data?.responseMessage, {
                    variant: "success",
                  });
                  navigate(PARTY_NUMBER);
                } else {
                  enqueueSnackbar(response.data?.responseMessage, {
                    variant: "error",
                  });
                }
              } catch (error) {
                enqueueSnackbar("An error occurred", { variant: "error" });
              }
              setSubmitting(false);
            }}
          >
            {({ values, errors, isSubmitting, setFieldValue }) => (
              <Form>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      sx={{ width: "100%" }}
                      name="name"
                      label="Party Name*"
                      value={values.name}
                      onChange={(e) => setFieldValue("name", e.target.value)}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      sx={{ width: "100%" }}
                      name="city"
                      label="City"
                      value={values.city}
                      onChange={(e) => setFieldValue("city", e.target.value)}
                      error={!!errors.city}
                      helperText={errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <JumboAutoComplete
                      options={userData?.list || []}
                      label="Belongs To*"
                      size="large"
                      getOptionLabel={(option) =>
                        option?.name || option?.email || ""
                      }
                      name="belongsTo"
                      value={values.belongsTo}
                      onChange={(evt, value) =>
                        setFieldValue("belongsTo", value)
                      }
                      helperText={errors?.belongsTo}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FieldArray name="phoneNumbers">
                      {({ push, remove }) => (
                        <>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography variant="h5" mb={0}>
                              Phone Numbers*
                            </Typography>
                            <IconButton
                              color="primary"
                              onClick={() => push("")}
                              size="small"
                            >
                              <AddCircleOutlineIcon />
                            </IconButton>
                          </Stack>
                          <Grid container spacing={2}>
                            {values.phoneNumbers.map((phone, index) => (
                              <Grid item xs={12} sm={6} key={index}>
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                  <TextField
                                    sx={{ flex: 1 }}
                                    label={`Phone Number ${index + 1}*`}
                                    value={phone}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `phoneNumbers[${index}]`,
                                        e.target.value
                                      )
                                    }
                                    error={
                                      !!(
                                        errors.phoneNumbers &&
                                        errors.phoneNumbers[index]
                                      )
                                    }
                                    helperText={
                                      errors.phoneNumbers &&
                                      errors.phoneNumbers[index]
                                    }
                                    inputProps={{ maxLength: 10 }}
                                  />
                                  {values.phoneNumbers.length > 1 && (
                                    <IconButton
                                      color="error"
                                      onClick={() => remove(index)}
                                      size="small"
                                      sx={{ mt: 1 }}
                                    >
                                      <RemoveCircleOutlineIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </FieldArray>
                  </Grid>
                </Grid>
                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%" }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => navigate(PARTY_NUMBER)}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default AddPartyNumber;
